import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// ROBOMIND website chatbot widget.
// - Renders NOTHING unless the admin dashboard has the chatbot enabled.
// - Hard GDPR consent gate before any message can be sent.
// - One ongoing conversation at a time (server-enforced); shows a wait
//   message when the single slot is busy.
// Talks only to this site's own backend (/api/chat*), which proxies to the
// AI box. The browser never contacts the AI box directly.

const BASE = process.env.REACT_APP_CHAT_API || '/api/chat';
const BRAND = '#0089d0';

const uuid = () =>
  (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });

export default function ChatWidget() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];

  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [consented, setConsented] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // queued = this message is waiting behind others in the FIFO queue.
  const [queued, setQueued] = useState(false);
  // closed = the server ended the conversation (contact gate); input disabled.
  const [closed, setClosed] = useState(false);

  // Mobile layout + on-screen-keyboard handling.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 640px)').matches
      : false
  );
  // Visual viewport tracks the area NOT covered by the keyboard.
  const [vp, setVp] = useState({ height: null, offsetTop: 0 });

  const sessionId = useRef(uuid());
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  // Guards double-send WITHOUT disabling the button mid-gesture (an element
  // mutating to `disabled` during the tap can cost focus on iOS).
  const sendingRef = useRef(false);

  // Pin the messages list to the bottom so the newest message sits just above
  // the input row. Immediate set (the effect runs post-commit, so the height is
  // already correct) plus deferred passes so it also catches the mobile
  // visualViewport resize when the keyboard shows/hides.
  const scrollToBottom = useCallback(() => {
    const stick = () => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; };
    stick();
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(stick);
    setTimeout(stick, 60);
    setTimeout(stick, 200);
  }, []);

  // Poll enable status. If disabled, the widget disappears entirely.
  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const r = await fetch(`${BASE}/status`, { cache: 'no-store' });
        const j = await r.json();
        if (stop) return;
        setEnabled(!!j.enabled);
      } catch (e) {
        if (!stop) setEnabled(false);
      }
    };
    poll();
    const id = setInterval(poll, 15000);
    return () => { stop = true; clearInterval(id); };
  }, []);

  // Release the slot + trigger the transcript email when the visitor leaves.
  useEffect(() => {
    const end = () => {
      if (!consented) return;
      try {
        const blob = new Blob([JSON.stringify({ sessionId: sessionId.current })], { type: 'application/json' });
        navigator.sendBeacon(`${BASE}/end`, blob);
      } catch (e) { /* noop */ }
    };
    window.addEventListener('beforeunload', end);
    return () => window.removeEventListener('beforeunload', end);
  }, [consented]);

  // Auto-scroll on: new message (user's or assistant's), typing/queued
  // indicator, and after the visual viewport height settles (keyboard show/hide).
  useEffect(() => {
    scrollToBottom();
  }, [messages, sending, queued, vp.height, scrollToBottom]);

  // Track breakpoint.
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, []);

  // Follow the visual viewport so the panel resizes when the keyboard shows/hides
  // and the input row stays pinned directly above the keyboard.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setVp({ height: vv.height, offsetTop: vv.offsetTop || 0 });
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // Lock background page scroll while the panel is open on mobile (no bleed-through).
  useEffect(() => {
    if (!(open && isMobile)) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open, isMobile]);

  const accept = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sessionId.current, lang, page: window.location.pathname }),
      });
      if (r.ok) {
        setConsented(true);
        setMessages([{ role: 'assistant', content: t('chatbot.greeting') }]);
      }
    } catch (e) { /* noop */ }
  }, [lang, t]);

  // Poll a queued/generating message until it finishes. While it waits behind
  // others we surface a "waiting your turn" state; once it's generating the
  // normal typing indicator shows.
  const pollResult = useCallback(async (jobId) => {
    const deadline = Date.now() + 150000; // client-side safety net
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    while (Date.now() < deadline) {
      await sleep(1200);
      let j;
      try {
        const r = await fetch(`${BASE}/result?jobId=${encodeURIComponent(jobId)}`, { cache: 'no-store' });
        if (r.status === 404) return { status: 'gone' };
        j = await r.json();
      } catch (e) { continue; }
      if (j.status === 'done' || j.status === 'error') return j;
      setQueued(j.status === 'queued' && (j.ahead || 0) > 0);
    }
    return { status: 'error', error: 'timeout' };
  }, []);

  // --- ASYNC part. Runs AFTER the gesture. Must never call focus()/blur() —
  //     iOS ignores focus() outside a user gesture and a stray blur would drop
  //     the keyboard. Purely network + state. ---
  const dispatchMessage = useCallback(async (text) => {
    try {
      const r = await fetch(BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sessionId.current, message: text, lang, page: window.location.pathname }),
      });
      if (r.status === 403) {
        const j = await r.json().catch(() => ({}));
        if (j.disabled) { setEnabled(false); return; }
        if (j.ended) { setClosed(true); return; } // server closed the conversation
        setConsented(false);
        return;
      }
      if (r.status === 503) { // queue full
        setMessages((m) => [...m, { role: 'system', content: t('chatbot.busy') }]);
        return;
      }
      if (!r.ok) {
        setMessages((m) => [...m, { role: 'system', content: t('chatbot.error') }]);
        return;
      }
      const { jobId, ahead } = await r.json();
      setQueued((ahead || 0) > 0);
      const result = await pollResult(jobId);
      if (result.status === 'done') {
        setMessages((m) => [...m, { role: 'assistant', content: result.reply }]);
        if (result.ended) setClosed(true); // graceful end from the contact gate
      } else if (result.status === 'error' && result.error === 'timeout') {
        setMessages((m) => [...m, { role: 'system', content: t('chatbot.timeout') }]);
      } else {
        setMessages((m) => [...m, { role: 'system', content: t('chatbot.error') }]);
      }
    } catch (e) {
      setMessages((m) => [...m, { role: 'system', content: t('chatbot.error') }]);
    } finally {
      sendingRef.current = false;
      setSending(false);
      setQueued(false);
      // NOTE: deliberately NO focus() here — this runs after `await`, so iOS
      // would ignore it anyway; the keyboard is kept alive by never losing
      // focus in the first place.
    }
  }, [lang, t, pollResult]);

  // --- SYNCHRONOUS gesture handler. Everything that can affect focus happens
  //     here, inside the user gesture, BEFORE any async work. ---
  const handleSend = useCallback(() => {
    const el = inputRef.current;
    const text = (el ? el.value : input).trim();
    if (!text || sendingRef.current || closed) return;

    // 1) Assert focus FIRST, synchronously inside the gesture (iOS rule).
    if (el) el.focus();

    // 2) Clear via controlled state only — same DOM node, never blurred.
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    sendingRef.current = true;
    setSending(true);
    setQueued(false);

    // 3) Scroll by writing scrollTop only (never scrollIntoView, which can
    //    move focus). Then re-assert focus, still synchronously.
    scrollToBottom();
    if (el) el.focus();

    // 4) Kick off the network work WITHOUT awaiting it here, so this handler
    //    stays synchronous from the browser's point of view.
    dispatchMessage(text);
  }, [input, closed, scrollToBottom, dispatchMessage]);

  if (!enabled) return null; // <-- widget fully hidden when chatbot is off

  const S = styles;
  // On phones the panel fills the DYNAMIC viewport (keyboard-aware) instead of
  // floating; desktop keeps the bottom-right floating card unchanged.
  const panelStyle = isMobile
    ? {
        ...styles.panel,
        position: 'fixed',
        left: 0,
        top: 0,
        right: 'auto',
        bottom: 'auto',
        width: '100vw',
        maxWidth: 'none',
        height: vp.height ? `${vp.height}px` : '100dvh',
        transform: vp.offsetTop ? `translateY(${vp.offsetTop}px)` : 'none',
        borderRadius: 0,
        border: 'none',
      }
    : styles.panel;
  return (
    <div style={S.root}>
      {open && (
        <div style={panelStyle} role="dialog" aria-label={t('chatbot.title')}>
          <div style={S.header}>
            <span style={S.headerTitle}>{t('chatbot.title')}</span>
            <button style={S.close} onClick={() => setOpen(false)} aria-label={t('chatbot.close')}>×</button>
          </div>

          {!consented ? (
            <div style={S.consent}>
              <p style={S.consentText}>{t('chatbot.consent_text')}</p>
              <a href="/privacy-policy" target="_blank" rel="noreferrer" style={S.link}>
                {t('chatbot.privacy_link')}
              </a>
              <button style={S.acceptBtn} onClick={accept}>{t('chatbot.accept')}</button>
            </div>
          ) : (
            <>
              <div style={S.messages} ref={scrollRef}>
                {messages.map((m, i) => (
                  <div key={i} style={m.role === 'user' ? S.userRow : S.botRow}>
                    <div style={m.role === 'user' ? S.userBubble : (m.role === 'system' ? S.sysBubble : S.botBubble)}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {sending && !queued && <div style={S.botRow}><div style={S.botBubble}>…</div></div>}
              </div>
              {queued && <div style={S.waitBar}>{t('chatbot.waiting_turn')}</div>}
              {/* NOT a <form>: iOS Safari dismisses the keyboard on native form
                  submission (the blur happens before onSubmit can preventDefault).
                  A plain row + type="button" avoids native submission entirely. */}
              <div style={S.inputRow}>
                <input
                  key="chat-input"           /* stable identity — never remounted */
                  ref={inputRef}
                  style={{ ...S.input, ...(closed ? S.inputClosed : null) }}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter submits without any native form submission.
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                  }}
                  enterKeyHint="send"
                  placeholder={closed ? t('chatbot.ended') : t('chatbot.placeholder')}
                  /* Only a CLOSED conversation disables it. Never disabled while
                     sending — toggling `disabled` on the focused node blurs it
                     and iOS drops the keyboard. Same node either way. */
                  disabled={closed}
                  aria-label={closed ? t('chatbot.ended') : t('chatbot.placeholder')}
                />
                <button
                  style={S.sendBtn}
                  type="button"              /* not submit — no native blur */
                  /* Stop the press from moving focus off the input. pointerdown
                     covers iOS; mousedown covers the synthesized/desktop path.
                     (Deliberately NOT touchstart — preventDefault there would
                     also cancel the click.) */
                  onPointerDown={(e) => e.preventDefault()}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleSend}
                  /* `sending` intentionally excluded: a button that becomes
                     disabled during the tap can cost focus on iOS. */
                  disabled={closed || !input.trim()}
                >
                  {t('chatbot.send')}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* On mobile the fullscreen panel has its own close (×) in the header, so
          hide the floating launcher while open to avoid it overlapping the input. */}
      {!(isMobile && open) && (
        <button style={S.launcher} onClick={() => setOpen((o) => !o)} aria-label={t('chatbot.title')}>
          {open ? '×' : '💬'}
        </button>
      )}
    </div>
  );
}

const styles = {
  root: { position: 'fixed', bottom: 20, right: 20, zIndex: 2147483000, fontFamily: 'Lato, system-ui, sans-serif' },
  launcher: {
    width: 56, height: 56, borderRadius: '50%', border: 'none', cursor: 'pointer',
    background: BRAND, color: '#fff', fontSize: 24, boxShadow: '0 6px 20px rgba(0,0,0,.25)',
  },
  panel: {
    position: 'absolute', bottom: 72, right: 0, width: 'min(92vw, 380px)', height: 'min(70vh, 560px)',
    background: '#fff', borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,.28)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #e5e7eb',
  },
  header: { background: BRAND, color: '#fff', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  headerTitle: { fontWeight: 700, fontSize: 15 },
  close: { background: 'transparent', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 },
  consent: { padding: 18, display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' },
  consentText: { fontSize: 13.5, color: '#374151', lineHeight: 1.5, margin: 0 },
  link: { fontSize: 13, color: BRAND, textDecoration: 'underline' },
  acceptBtn: { background: BRAND, color: '#fff', border: 'none', borderRadius: 9, padding: '10px 14px', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  messages: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', padding: 14, display: 'flex', flexDirection: 'column', gap: 8, background: '#f8fafc' },
  userRow: { display: 'flex', justifyContent: 'flex-end' },
  botRow: { display: 'flex', justifyContent: 'flex-start' },
  userBubble: { background: BRAND, color: '#fff', padding: '8px 12px', borderRadius: '12px 12px 2px 12px', maxWidth: '82%', fontSize: 14, whiteSpace: 'pre-wrap' },
  botBubble: { background: '#fff', color: '#111827', padding: '8px 12px', borderRadius: '12px 12px 12px 2px', maxWidth: '82%', fontSize: 14, border: '1px solid #e5e7eb', whiteSpace: 'pre-wrap' },
  sysBubble: { background: '#fef3c7', color: '#92400e', padding: '6px 10px', borderRadius: 8, maxWidth: '90%', fontSize: 12.5 },
  waitBar: { background: '#fef3c7', color: '#92400e', fontSize: 12.5, padding: '6px 12px', textAlign: 'center' },
  inputRow: { display: 'flex', gap: 6, padding: 10, borderTop: '1px solid #e5e7eb', background: '#fff', alignItems: 'center', flexWrap: 'nowrap', boxSizing: 'border-box' },
  // minWidth:0 lets the input actually shrink so the send button is never
  // pushed off the right edge; the button never shrinks and never wraps.
  input: { flex: '1 1 auto', minWidth: 0, border: '1px solid #d1d5db', borderRadius: 9, padding: '9px 11px', fontSize: 16, outline: 'none', boxSizing: 'border-box' },
  inputClosed: { background: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed' },
  sendBtn: { flex: '0 0 auto', flexShrink: 0, whiteSpace: 'nowrap', background: BRAND, color: '#fff', border: 'none', borderRadius: 9, padding: '10px 14px', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
};
